import React, { useState, useEffect } from 'react';
import { Mail, Phone, MapPin } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import emailjs from '@emailjs/browser';
import Social from '../components/Social';

// TODO: Replace these with your own EmailJS credentials from https://dashboard.emailjs.com
// These are example credentials and will not work
const PUBLIC_KEY = process.env.REACT_APP_EMAILJS_PUBLIC_KEY || 'YOUR_PUBLIC_KEY';
const SERVICE_ID = process.env.REACT_APP_EMAILJS_SERVICE_ID || 'YOUR_SERVICE_ID';
const TEMPLATE_ID = process.env.REACT_APP_EMAILJS_TEMPLATE_ID || 'YOUR_TEMPLATE_ID';

const Contact = () => {
  const { t } = useTranslation();
  
  // Initialize EmailJS - Replace with your own Public Key from EmailJS Dashboard
  useEffect(() => {
    emailjs.init(PUBLIC_KEY); // Get from: https://dashboard.emailjs.com/admin/account
  }, []);
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
    service: 'website'
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    const { name, email, phone, message, service } = formData;
    
    // Validate form
    if (!name || !email || !message || !service) {
      setSubmitStatus('error');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('');

    try {
      // Check if EmailJS is properly configured
      if (PUBLIC_KEY === 'YOUR_PUBLIC_KEY' || SERVICE_ID === 'YOUR_SERVICE_ID' || TEMPLATE_ID === 'YOUR_TEMPLATE_ID') {
        console.warn('EmailJS not configured. Please set up your credentials.');
        throw new Error('EmailJS not configured');
      }

      // Format message with all contact details
      const contactMessage = `
Contact Information:
Name: ${name}
Email: ${email}
Phone: ${phone || 'Not provided'}
Service Requested: ${service}

Message:
${message}`;

      await emailjs.send(
        SERVICE_ID,
        TEMPLATE_ID,
        {
          to_name: 'Arlene Tarot',
          from_name: name,
          from_email: email,
          message: contactMessage,
          subject: `New ${service} inquiry from ${name}`,
        },
        PUBLIC_KEY
      );

      setSubmitStatus('success');
      setFormData({
        name: '',
        email: '',
        phone: '',
        message: '',
        service: 'website'
      });
    } catch (err) {
      setSubmitStatus('error');
      console.error('Failed to send email:', err);
      console.error('Error details:', err.text || err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 to-white">

      <section className="py-16 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 mb-16">
            <div className="glass-card rounded-2xl shadow-xl p-8" data-aos="fade-right">
              <h2 className="text-3xl font-bold text-pink-400 mb-6">{t('contact.sendMessage')}</h2>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-semibold text-gray-600 mb-2">
                    {t('contact.name')} {t('common.required')}
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 bg-white border-2 border-gray-800 text-gray-600 rounded-lg focus:ring-2 focus:ring-lime-600 focus:border-pink-600 placeholder-stone-500"
                    placeholder={t('contact.namePlaceholder')}
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-semibold text-gray-600 mb-2">
                    {t('contact.email')} {t('common.required')}
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 bg-white border-2 border-gray-800 text-gray-600 rounded-lg focus:ring-2 focus:ring-lime-600 focus:border-pink-600 placeholder-stone-500 outline-none transition-all duration-200"
                    placeholder={t('contact.emailPlaceholder')}
                  />
                </div>

                <div>
                  <label htmlFor="phone" className="block text-sm font-semibold text-gray-600 mb-2">
                    {t('contact.phone')}
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-white border-2 border-gray-800 text-gray-600 rounded-lg focus:ring-2 focus:ring-lime-600 focus:border-pink-600 placeholder-stone-500 outline-none transition-all duration-200"
                    placeholder={t('contact.phonePlaceholder')}
                  />
                </div>

                <div>
                  <label htmlFor="service" className="block text-sm font-semibold text-gray-600 mb-2">
                    {t('contact.service')} {t('common.required')}
                  </label>
                  <select
                    id="service"
                    name="service"
                    value={formData.service}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 bg-white border-2 border-gray-800 text-gray-600 rounded-lg focus:ring-2 focus:ring-lime-600 focus:border-pink-600"
                  >
                    <option value="">{t('contact.selectService')}</option>
                    <option value="tarot">{t('contact.tarotService')}</option>
                    <option value="astrology">{t('contact.astroService')}</option>
                    <option value="cuba">{t('contact.cubaService')}</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-semibold text-gray-600 mb-2">
                    {t('contact.message')} {t('common.required')}
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    rows="5"
                    required
                    className="w-full px-4 py-3 bg-white border-2 border-gray-800 text-gray-600 rounded-lg focus:ring-2 focus:ring-lime-600 focus:border-pink-600 placeholder-stone-500"
                    placeholder={t('contact.messagePlaceholder')}
                    value={formData.message}
                    onChange={handleChange}
                  ></textarea>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-gradient-to-r from-primary-300 to-pink-400 hover:from-primary-400 hover:to-pink-500 text-gray-600 px-8 py-4 rounded-full font-semibold transition-all duration-200 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
                >
                  {isSubmitting ? t('contact.sending') : t('contact.sendButton')}
                </button>

                {submitStatus === 'success' && (
                  <div className="mt-4 p-4 bg-green-100 border border-green-500 text-green-800 rounded-lg">
                    {t('contact.successMessage')}
                  </div>
                )}
                {submitStatus === 'error' && (
                  <div className="mt-4 p-4 bg-red-100 border border-red-500 text-red-800 rounded-lg">
                    <p className="font-semibold mb-2">There was an error sending your message.</p>
                    <p className="text-sm">Please contact me directly via:</p>
                    <div className="mt-2 space-y-1 text-sm">
                      <p>📱 WhatsApp: <a href="https://wa.me/5358026042" className="underline hover:text-red-200">+53 58026042</a></p>
                      <p>📧 Email: <a href="mailto:arlene.set2012@gmail.com" className="underline hover:text-red-200">arlene.set2012@gmail.com</a></p>
                    </div>
                  </div>
                )}
              </form>
            </div>

            <div className="space-y-6">
              <div className="glass-card rounded-xl shadow-lg p-6" data-aos="fade-left" data-aos-delay="100">
                <h3 className="text-2xl font-bold mb-4 text-pink-400">{t('contact.contactInfo')}</h3>
                <div className="space-y-4">
                  <div className="flex items-start space-x-3">
                    <Phone className="w-6 h-6 text-primary-600 flex-shrink-0 mt-1" />
                    <div>
                      <p className="font-semibold text-gray-600">{t('contact.whatsapp')}</p>
                      <p className="text-gray-600">+53 58026042</p>
                      <p className="text-gray-600">+53 63131261</p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-3">
                    <Mail className="w-6 h-6 text-primary-600 flex-shrink-0 mt-1" />
                    <div>
                      <p className="font-semibold text-gray-600">{t('contact.email')}</p>
                      <p className="text-gray-600">arlene.set2012@gmail.com</p>
                    </div>
                  </div>

                  <div>
                    <p className="font-semibold text-gray-600 mb-3">{t('contact.socialMedia')}</p>
                    <Social iconSize="w-7 h-7" />
                    <p className="text-gray-600 mt-3 text-sm">@tarotdearlene</p>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-br from-primary-200 to-pink-300 text-gray-600 rounded-xl shadow-lg p-6" data-aos="fade-left" data-aos-delay="200">
                <h3 className="text-xl font-bold mb-4 text-pink-400">{t('contact.location')}</h3>
                <div className="flex items-center space-x-3">
                  <MapPin className="w-6 h-6 flex-shrink-0" />
                  <p>{t('contact.havana')}</p>
                </div>
              </div>

              <div className="aspect-video rounded-xl overflow-hidden shadow-lg" data-aos="zoom-in" data-aos-delay="300">
                <iframe
                  width="100%"
                  height="100%"
                  src="https://www.youtube.com/embed/0soig_qMFf8"
                  title="Arlene Tarot Short"
                  frameBorder="0"
                  allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  className="w-full h-full"
                ></iframe>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
